import React, { useEffect, useState, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import api from '../utils/api';
import { Btn, Badge, Modal, Input, Select, Loading, EmptyState } from '../components/ui';

export default function OperationMaster() {
  const [operations, setOperations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [machineTypes, setMachineTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterDivision, setFilterDivision] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', sam: '', op_type: 'Machine', fundamental_group_id: '', division_id: '', machine_sams: [{ machine_type: '', sam: '' }] });
  const [error, setError] = useState('');
  const [uploadMsg, setUploadMsg] = useState('');
  const fileRef = useRef();

  useEffect(() => { document.getElementById('lc-page-title').textContent = 'Operation master'; }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterDivision ? { division_id: filterDivision } : {};
      const [ops, grps, divs, machines] = await Promise.all([
        api.get('/operations', { params }),
        api.get('/fundamental-groups'),
        api.get('/divisions'),
        api.get('/machine-types')
      ]);
      setOperations(ops.data);
      setGroups(grps.data);
      setDivisions(divs.data);
      // Distinct machine type codes across all factories — a machine type
      // code (SNLS, OL, etc.) is a standard descriptor, not factory-specific,
      // so we don't scope this to the operation's own division.
      setMachineTypes([...new Set(machines.data.map(m => m.machine_type))].sort());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterDivision]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = operations.filter(o => {
    const matchSearch = !search || o.name.toLowerCase().includes(search.toLowerCase());
    const matchType = !filterType || o.op_type === filterType;
    const matchGroup = !filterGroup || String(o.fundamental_group_id) === String(filterGroup);
    return matchSearch && matchType && matchGroup;
  });

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', sam: '', op_type: 'Machine', fundamental_group_id: groups[0]?.id || '', division_id: filterDivision || '', machine_sams: [{ machine_type: '', sam: '' }] });
    setError(''); setModalOpen(true);
  };

  const openEdit = (o) => {
    setEditing(o);
    const existingMachineSams = (o.machine_sams && o.machine_sams.length)
      ? o.machine_sams.map(m => ({ machine_type: m.machine_type, sam: String(m.sam) }))
      : [{ machine_type: '', sam: '' }];
    setForm({ name: o.name, sam: o.sam, op_type: o.op_type, fundamental_group_id: o.fundamental_group_id, division_id: o.division_id || '', machine_sams: existingMachineSams });
    setError(''); setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.fundamental_group_id) { setError('Name and fundamental group are required'); return; }

    let payload = { ...form, division_id: form.division_id || null };

    if (form.op_type === 'Non-machine') {
      if (!form.sam || isNaN(form.sam) || parseFloat(form.sam) <= 0) { setError('SAM must be a positive number'); return; }
      payload.sam = parseFloat(form.sam);
      payload.machine_sams = [];
    } else {
      const cleanRows = form.machine_sams.filter(m => m.machine_type.trim() && m.sam);
      if (!cleanRows.length) { setError('Add at least one machine type with SAM'); return; }
      if (cleanRows.some(m => isNaN(m.sam) || parseFloat(m.sam) <= 0)) { setError('Each SAM must be a positive number'); return; }
      const seen = new Set();
      for (const m of cleanRows) {
        const key = m.machine_type.trim().toUpperCase();
        if (seen.has(key)) { setError(`Machine type "${key}" is listed more than once`); return; }
        seen.add(key);
      }
      payload.machine_sams = cleanRows.map(m => ({ machine_type: m.machine_type.trim(), sam: parseFloat(m.sam) }));
      delete payload.sam;
    }

    try {
      if (editing) await api.put(`/operations/${editing.id}`, payload);
      else await api.post('/operations', payload);
      setModalOpen(false); fetchAll();
    } catch (e) { setError(e.response?.data?.error || 'Error saving'); }
  };

  const handleToggle = async (op) => {
    try { await api.put(`/operations/${op.id}`, { ...op, is_active: !op.is_active }); fetchAll(); }
    catch (e) { alert(e.response?.data?.error || 'Error'); }
  };

  const addMachineRow = () => setForm(p => ({ ...p, machine_sams: [...p.machine_sams, { machine_type: '', sam: '' }] }));
  const removeMachineRow = (idx) => setForm(p => ({ ...p, machine_sams: p.machine_sams.filter((_, i) => i !== idx) }));
  const updateMachineRow = (idx, field, value) => setForm(p => ({
    ...p, machine_sams: p.machine_sams.map((m, i) => i === idx ? { ...m, [field]: value } : m)
  }));

  // ── Download template with enforced dropdowns
  const downloadTemplate = async () => {
    const groupNames = groups.map(g => g.name);
    const divisionNames = divisions.map(d => d.name);
    const typeValues = ['Machine', 'Non-machine'];
    const machineValues = machineTypes.length ? machineTypes : ['—'];

    const wb = new ExcelJS.Workbook();

    // Sheet 1: Data entry
    const ws = wb.addWorksheet('Operations');
    ws.columns = [
      { header: 'Operation Name',    key: 'name',    width: 32 },
      { header: 'Machine Type',      key: 'machine', width: 16 },
      { header: 'SAM',               key: 'sam',     width: 10 },
      { header: 'Type',              key: 'type',    width: 16 },
      { header: 'Fundamental Group', key: 'group',   width: 22 },
      { header: 'Division',          key: 'div',     width: 20 },
    ];

    // Style header
    ws.getRow(1).eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF185FA5' } };
    });

    // Sample rows — Machine-type operations get one row PER machine type
    // (same operation name repeated, each with its own SAM). Non-machine
    // operations get a single row with Machine Type left blank.
    ws.addRow(['Attach collar',    'OL', 2.10, 'Machine',     'Attach',  '']);
    ws.addRow(['Attach collar',    'FL', 1.95, 'Machine',     'Attach',  '']);
    ws.addRow(['Bottom hem',       'OL', 1.15, 'Machine',     'Hem',     '']);
    ws.addRow(['Mark & cut front', '',   0.45, 'Non-machine', 'Profile', '']);

    // Sheet 2: Lists (hidden)
    const wsLists = wb.addWorksheet('Lists');
    wsLists.state = 'hidden';

    typeValues.forEach((v, i)    => { wsLists.getCell(`A${i + 1}`).value = v; });
    groupNames.forEach((v, i)    => { wsLists.getCell(`B${i + 1}`).value = v; });
    divisionNames.forEach((v, i) => { wsLists.getCell(`C${i + 1}`).value = v; });
    machineValues.forEach((v, i) => { wsLists.getCell(`D${i + 1}`).value = v; });

    const typeFormula     = `Lists!$A$1:$A$${typeValues.length}`;
    const groupFormula    = `Lists!$B$1:$B$${groupNames.length}`;
    const divisionFormula = `Lists!$C$1:$C$${divisionNames.length}`;
    const machineFormula  = `Lists!$D$1:$D$${machineValues.length}`;

    // Apply data validation rows 2..1000 — note columns shifted right by one
    // (B) since Machine Type is now its own column.
    for (let row = 2; row <= 1000; row++) {
      ws.getCell(`B${row}`).dataValidation = {
        type: 'list', allowBlank: true, formulae: [machineFormula],
        showErrorMessage: true, errorStyle: 'stop',
        errorTitle: 'Invalid value', error: 'Please select a valid machine type, or leave blank for Non-machine operations.',
      };
      ws.getCell(`D${row}`).dataValidation = {
        type: 'list', allowBlank: false, formulae: [typeFormula],
        showErrorMessage: true, errorStyle: 'stop',
        errorTitle: 'Invalid value', error: 'Please select Machine or Non-machine.',
      };
      ws.getCell(`E${row}`).dataValidation = {
        type: 'list', allowBlank: false, formulae: [groupFormula],
        showErrorMessage: true, errorStyle: 'stop',
        errorTitle: 'Invalid value', error: 'Please select a valid fundamental group.',
      };
      ws.getCell(`F${row}`).dataValidation = {
        type: 'list', allowBlank: true, formulae: [divisionFormula],
        showErrorMessage: true, errorStyle: 'stop',
        errorTitle: 'Invalid value', error: 'Please select a valid division.',
      };
    }

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'operation_upload_template.xlsx'; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Download all operations — Machine-type ops export one row per machine,
  // matching the template format so a re-upload round-trips correctly.
  const downloadAll = () => {
    const rows = [];
    operations.forEach(o => {
      if (o.op_type === 'Non-machine' || !o.machine_sams?.length) {
        rows.push({
          'Operation Name':    o.name,
          'Machine Type':      '',
          'SAM':               parseFloat(o.sam).toFixed(3),
          'Type':              o.op_type,
          'Fundamental Group': o.group_name || '',
          'Division':          o.division_name || '',
          'Status':            o.is_active ? 'Active' : 'Inactive',
        });
      } else {
        o.machine_sams.forEach(m => {
          rows.push({
            'Operation Name':    o.name,
            'Machine Type':      m.machine_type,
            'SAM':               parseFloat(m.sam).toFixed(3),
            'Type':              o.op_type,
            'Fundamental Group': o.group_name || '',
            'Division':          o.division_name || '',
            'Status':            o.is_active ? 'Active' : 'Inactive',
          });
        });
      }
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [{ wch: 30 }, { wch: 14 }, { wch: 8 }, { wch: 15 }, { wch: 20 }, { wch: 18 }, { wch: 10 }];
    const wb2 = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb2, ws, 'Operations');
    XLSX.writeFile(wb2, 'operations.xlsx');
  };

  // ── Upload Excel
  const handleUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    setUploadMsg('Uploading…');
    try {
      const { data } = await api.post('/operations/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadMsg(`✓ Inserted ${data.inserted} records${data.errors?.length ? ` · ${data.errors.length} skipped` : ''}`);
      fetchAll();
    } catch (e) {
      setUploadMsg('Upload failed: ' + (e.response?.data?.error || e.message));
    }
    e.target.value = '';
  };

  const groupBadgeType = (name) => {
    const map = { Hem: 'green', Attach: 'amber', Topstitch: 'gray', 'Edge stitch': 'gray', Folder: 'gray', Profile: 'blue' };
    return map[name] || 'gray';
  };

  return (
    <div style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 500 }}>Operation master <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>{filtered.length} records</span></div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>All garment operations with SAM values</div>
        </div>
        <input className="inp" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} style={{ width: 170 }} />
        <Select value={filterDivision} onChange={setFilterDivision} placeholder="All divisions"
          options={divisions.map(d => ({ value: d.id, label: d.name }))} />
        <Select value={filterType} onChange={setFilterType} placeholder="All types"
          options={[{ value: 'Machine', label: 'Machine' }, { value: 'Non-machine', label: 'Non-machine' }]} />
        <Select value={filterGroup} onChange={setFilterGroup} placeholder="All groups"
          options={groups.map(g => ({ value: g.id, label: g.name }))} />
        <div style={{ display: 'flex', gap: 7 }}>
          <Btn size="sm" onClick={downloadTemplate}>Download template</Btn>
          <Btn size="sm" onClick={() => fileRef.current.click()}>Upload Excel</Btn>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleUpload} />
          <Btn size="sm" onClick={downloadAll}>Download all</Btn>
          <Btn variant="primary" size="sm" onClick={openAdd}>+ Add new</Btn>
        </div>
      </div>

      <div style={{
        fontSize: 12, color: 'var(--acc)', background: 'var(--acc-bg)',
        border: '0.5px solid var(--acc)', borderRadius: 8, padding: '8px 12px', marginBottom: 10
      }}>
        <strong>Naming convention:</strong> Maintain operations as <em>Product part + Operation</em>. E.g. Sleeve Attach, Bottom Hem. Do not use short forms.
      </div>

      {uploadMsg && (
        <div style={{ marginBottom: 10, fontSize: 12, padding: '6px 12px', borderRadius: 7,
          background: uploadMsg.startsWith('✓') ? 'var(--grn-bg)' : 'var(--red-bg)',
          color: uploadMsg.startsWith('✓') ? 'var(--grn)' : 'var(--red)' }}>
          {uploadMsg}
        </div>
      )}

      <div style={{ marginBottom: 12, padding: '10px 14px', background: 'var(--bg-secondary)', borderRadius: 8, fontSize: 11.5, color: 'var(--text-secondary)' }}>
        <strong style={{ color: 'var(--text-primary)' }}>Excel format:</strong>&nbsp;
        <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace' }}>Operation Name</code> ·&nbsp;
        <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace' }}>Machine Type</code> ·&nbsp;
        <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace' }}>SAM</code> ·&nbsp;
        <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace' }}>Type</code> ·&nbsp;
        <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace' }}>Fundamental Group</code> ·&nbsp;
        <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace' }}>Division</code>
        &nbsp;— Machine Type, Fundamental Group and Division are enforced dropdowns. For Machine-type operations that run on more than one machine, repeat the operation name on multiple rows — one row per machine, each with its own SAM. Leave Machine Type blank for Non-machine operations.
      </div>

      <div style={{ border: '0.5px solid var(--border-md)', borderRadius: 10, overflow: 'hidden', background: 'var(--bg-primary)' }}>
        {loading ? <Loading /> : filtered.length === 0
          ? <EmptyState message="No operations found" action={<Btn variant="primary" onClick={openAdd}>+ Add operation</Btn>} />
          : (
            <table className="lc-table">
              <thead>
                <tr>
                  <th>Operation name</th>
                  <th>SAM (min)</th>
                  <th>Type</th>
                  <th>Fund. group</th>
                  <th>Division</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => (
                  <tr key={o.id}>
                    <td style={{ fontWeight: 500 }}>{o.name}</td>
                    <td style={{ fontWeight: 500, color: 'var(--acc)' }}>
                      {o.op_type === 'Non-machine' || !o.machine_sams?.length ? (
                        parseFloat(o.sam).toFixed(2)
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {o.machine_sams.map((m, i) => (
                            <span key={i} style={{ fontSize: 11.5 }}>{m.machine_type}: {parseFloat(m.sam).toFixed(2)}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td><Badge type={o.op_type === 'Machine' ? 'blue' : 'gray'}>{o.op_type}</Badge></td>
                    <td><Badge type={groupBadgeType(o.group_name)}>{o.group_name || '—'}</Badge></td>
                    <td style={{ fontSize: 12 }}>{o.division_name || '—'}</td>
                    <td><Badge type={o.is_active ? 'green' : 'red'}>{o.is_active ? 'Active' : 'Inactive'}</Badge></td>
                    <td style={{ display: 'flex', gap: 10 }}>
                      <a style={{ fontSize: 11, color: 'var(--acc)', cursor: 'pointer' }} onClick={() => openEdit(o)}>Edit</a>
                      <a style={{ fontSize: 11, color: o.is_active ? 'var(--red)' : 'var(--grn)', cursor: 'pointer' }} onClick={() => handleToggle(o)}>
                        {o.is_active ? 'Deactivate' : 'Activate'}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)}
        title={editing ? 'Edit operation' : 'Add operation'} width={400}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontSize: 11, color: 'var(--acc)', background: 'var(--acc-bg)', borderRadius: 6, padding: '6px 9px' }}>
            Use <em>Product part + Operation</em> — e.g. Sleeve Attach, Bottom Hem. No short forms.
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Operation name</div>
            <Input value={form.name} onChange={v => setForm(p => ({ ...p, name: v }))}
              placeholder="e.g. Sleeve Attach" style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Type of operation</div>
            <Select value={form.op_type} onChange={v => setForm(p => ({ ...p, op_type: v }))}
              options={[{ value: 'Machine', label: 'Machine' }, { value: 'Non-machine', label: 'Non-machine' }]}
              style={{ width: '100%' }} />
          </div>
          {form.op_type === 'Non-machine' ? (
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>SAM (minutes)</div>
              <Input value={form.sam} onChange={v => setForm(p => ({ ...p, sam: v }))}
                type="number" placeholder="e.g. 1.85" style={{ width: '100%' }} />
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>
                SAM by machine type — this operation can take a different standard time depending on the machine used
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {form.machine_sams.map((m, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Select value={m.machine_type} onChange={v => updateMachineRow(idx, 'machine_type', v)}
                      placeholder="Select machine…"
                      options={[...new Set([...machineTypes, ...(m.machine_type ? [m.machine_type] : [])])].map(mt => ({ value: mt, label: mt }))}
                      style={{ flex: 1 }} />
                    <Input value={m.sam} onChange={v => updateMachineRow(idx, 'sam', v)}
                      type="number" placeholder="SAM" style={{ width: 90 }} />
                    <button onClick={() => removeMachineRow(idx)} disabled={form.machine_sams.length <= 1}
                      style={{
                        padding: '5px 9px', borderRadius: 6, border: 'none', cursor: form.machine_sams.length <= 1 ? 'default' : 'pointer',
                        color: form.machine_sams.length <= 1 ? '#bbb' : 'var(--red)',
                        background: form.machine_sams.length <= 1 ? '#f5f5f5' : 'var(--red-bg)', fontWeight: 700
                      }}>✕</button>
                  </div>
                ))}
              </div>
              <a onClick={addMachineRow} style={{ fontSize: 11.5, color: 'var(--acc)', cursor: 'pointer', display: 'inline-block', marginTop: 6 }}>+ Add another machine type</a>
            </div>
          )}
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Division</div>
            <Select value={form.division_id} onChange={v => setForm(p => ({ ...p, division_id: v }))}
              placeholder="Select division…" options={divisions.map(d => ({ value: d.id, label: d.name }))}
              style={{ width: '100%' }} />
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Fundamental group</div>
            <Select value={form.fundamental_group_id} onChange={v => setForm(p => ({ ...p, fundamental_group_id: v }))}
              placeholder="Select group…" options={groups.map(g => ({ value: g.id, label: g.name }))}
              style={{ width: '100%' }} />
          </div>
          {error && <div style={{ fontSize: 11.5, color: 'var(--red)', background: 'var(--red-bg)', padding: '6px 9px', borderRadius: 6 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
            <Btn onClick={() => setModalOpen(false)}>Cancel</Btn>
            <Btn variant="primary" onClick={handleSave}>Save</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}